import { useCallback } from 'react'
import { cn } from '@/true-fsd/shared/utils/css'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { ColorPickerPopup } from './ColorPickerPopup'
import { BackgroundPaletteIcon } from './icons/BackgroundPaletteIcon'
import classes from './BackgroundColorButton.module.scss'

/**
 * Пропсы для компонента BackgroundColorButton
 */
export interface BackgroundColorButtonProps {
    /**
     * Текущий цвет подложки в формате hex с alpha (например, "#056FFF80")
     */
    value: string | null
    /**
     * Обработчик изменения цвета подложки
     */
    onChange?: (color: string) => void
    /**
     * Дополнительные CSS классы
     */
    className?: string
    /**
     * Отключена ли кнопка
     */
    disabled?: boolean
    /**
     * Сохраненные кастомные цвета для подложки (максимум 4)
     */
    customColors?: string[]
    /**
     * Обработчик изменения сохраненных кастомных цветов для подложки
     */
    onCustomColorsChange?: (colors: string[]) => void
}

/**
 * Компонент кнопки выбора цвета подложки
 * При наведении отображает тултип "Цвет фона"
 * При клике открывает попап выбора цвета
 * Отображается только когда подложка включена
 */
export function BackgroundColorButton({
    value,
    onChange,
    className,
    disabled = false,
    customColors = [],
    onCustomColorsChange,
}: BackgroundColorButtonProps) {
    /**
     * Обработчик изменения цвета
     */
    const handleColorChange = useCallback(
        (color: string) => {
            if (disabled || !onChange) {
                return
            }
            onChange(color)
        },
        [disabled, onChange]
    )

    const button = (
        <button
            type="button"
            className={cn(classes.button, className, {
                [classes.buttonDisabled]: disabled,
            })}
            disabled={disabled}
            aria-label="Выбрать цвет подложки"
        >
            <BackgroundPaletteIcon className={classes.icon} />
        </button>
    )

    // Если есть onChange, оборачиваем в ColorPickerPopup с Tooltip внутри
    if (onChange) {
        return (
            <ColorPickerPopup
                value={value || '#FFFFFFFF'}
                onChange={handleColorChange}
                customColors={customColors}
                onCustomColorsChange={onCustomColorsChange}
            >
                <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent
                        side="top"
                        sideOffset={4}
                        className="z-[60]"
                    >
                        Цвет фона
                    </TooltipContent>
                </Tooltip>
            </ColorPickerPopup>
        )
    }

    // Если нет onChange, просто кнопка с тултипом
    return (
        <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="top" sideOffset={4} className="z-[60]">
                Цвет фона
            </TooltipContent>
        </Tooltip>
    )
}

